/**
 * Load and save one bot's settings.
 *
 * Saving is per-panel: `save` sends only the fields handed to it, because the
 * backend leaves everything else untouched. A panel that posted the whole
 * object would silently overwrite edits made in another panel by whoever
 * loaded the page first.
 */

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api/client';
import type { Bot, BotSettings } from '../api/types';

interface BotSettingsState {
  bot: Bot | null;
  settings: BotSettings | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  savedAt: number | null;
  /** Update the local copy without saving, for live preview while typing. */
  patch: (changes: Partial<BotSettings>) => void;
  /** Persist the given fields and adopt the server's response. */
  save: (changes: Partial<BotSettings>) => Promise<void>;
  reload: () => Promise<void>;
}

export function useBotSettings(slug: string | null): BotSettingsState {
  const [bot, setBot] = useState<Bot | null>(null);
  const [settings, setSettings] = useState<BotSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const reload = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const loaded = await api.getBot(slug);
      setBot(loaded);
      setSettings(loaded.settings);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load settings');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const patch = useCallback((changes: Partial<BotSettings>) => {
    setSettings((current) => (current ? { ...current, ...changes } : current));
  }, []);

  const save = useCallback(
    async (changes: Partial<BotSettings>) => {
      if (!slug) return;
      setSaving(true);
      setError(null);
      try {
        const updated = await api.saveBotSettings(slug, changes);
        setBot(updated);
        setSettings(updated.settings);
        setSavedAt(Date.now());
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Could not save');
      } finally {
        setSaving(false);
      }
    },
    [slug],
  );

  return { bot, settings, loading, error, saving, savedAt, patch, save, reload };
}
